import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    CheckCircle2,
    Clock,
    FileCheck2,
    RefreshCw,
    Scale,
    Send,
    Shield,
    Trash2,
    XCircle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { whatsappMerkez } from "../../lib/api";

const DURUM_RENK = {
    BEKLIYOR: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    YANIT_BEKLIYOR: "text-sky-300 bg-sky-500/10 border-sky-500/30",
    ONAYLANDI: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    REDDEDILDI: "text-rose-300 bg-rose-500/10 border-rose-500/30",
    SURE_DOLDU: "text-slate-400 bg-slate-500/10 border-slate-500/30",
    IPTAL: "text-slate-400 bg-slate-500/10 border-slate-500/30",
};

function DurumPill({ durum, label }) {
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${DURUM_RENK[durum] || DURUM_RENK.BEKLIYOR}`}
        >
            {label || durum}
        </span>
    );
}

export default function YatarOnayPanel({ runBusy, busyKey }) {
    const [ozet, setOzet] = useState(null);
    const [kayitlar, setKayitlar] = useState([]);
    const [filtre, setFiltre] = useState("");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [secili, setSecili] = useState(null);
    const [denetim, setDenetim] = useState([]);

    const yukle = useCallback(async () => {
        setLoading(true);
        try {
            const [oz, liste] = await Promise.all([
                whatsappMerkez.yatarOnayOzet(),
                whatsappMerkez.yatarOnayListe({ durum: filtre || undefined, q: q || undefined }),
            ]);
            setOzet(oz?.ozet || oz);
            setKayitlar(liste?.kayitlar || []);
        } catch (e) {
            toast.error("Yatar onay listesi yüklenemedi.");
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [filtre, q]);

    useEffect(() => {
        yukle();
    }, [yukle]);

    const denetimYukle = async (kayitId) => {
        try {
            const res = await whatsappMerkez.yatarOnayDenetim(kayitId);
            setDenetim(res?.denetim || []);
        } catch {
            setDenetim([]);
        }
    };

    const secKayit = (row) => {
        setSecili(row);
        denetimYukle(row.id);
    };

    const testGonder = async () => {
        await runBusy("yatar-test", async () => {
            const res = await whatsappMerkez.yatarOnayTest({});
            if (res?.ok) {
                toast.success(res.detail || "Test mesajı gönderildi.", {
                    description: `Hedef: ${res.telefon}${res.test_modu ? " (test modu aktif)" : ""}`,
                });
                yukle();
            } else {
                toast.error(res?.detail || "Test gönderilemedi.");
            }
        });
    };

    const panelOnay = async (id) => {
        await runBusy(`yatar-onay-${id}`, async () => {
            const res = await whatsappMerkez.yatarOnayPanelOnay(id);
            if (res?.ok) {
                toast.success("Panel onayı kaydedildi.");
                yukle();
                if (secili?.id === id) denetimYukle(id);
            } else {
                toast.error(res?.detail || "Onay kaydedilemedi.");
            }
        });
    };

    const panelRed = async (id) => {
        await runBusy(`yatar-red-${id}`, async () => {
            const res = await whatsappMerkez.yatarOnayPanelRed(id, { notu: "Panel üzerinden reddedildi." });
            if (res?.ok) {
                toast.success("Red kaydedildi.");
                yukle();
                if (secili?.id === id) denetimYukle(id);
            } else {
                toast.error(res?.detail || "Red kaydedilemedi.");
            }
        });
    };

    const hatirlatGonder = async (id) => {
        await runBusy(`yatar-hat-${id}`, async () => {
            const res = await whatsappMerkez.yatarOnayHatirlat(id);
            if (res?.ok) {
                toast.success(res.detail || "Hatırlatma gönderildi.");
                yukle();
                if (secili?.id === id) denetimYukle(id);
            } else {
                toast.error(res?.detail || "Hatırlatma gönderilemedi.");
            }
        });
    };

    const kayitSil = async (id) => {
        if (!window.confirm("Bu yatar onay kaydını silmek istediğinize emin misiniz?")) {
            return;
        }
        await runBusy(`yatar-sil-${id}`, async () => {
            const res = await whatsappMerkez.yatarOnaySil(id);
            if (res?.ok) {
                toast.success("Kayıt silindi.");
                if (secili?.id === id) {
                    setSecili(null);
                    setDenetim([]);
                }
                yukle();
            } else {
                toast.error(res?.detail || "Kayıt silinemedi.");
            }
        });
    };

    const kartlar = [
        { key: "onaylanan", label: "Onaylayan", icon: CheckCircle2, tone: "text-emerald-400" },
        { key: "yanit_bekleyen", label: "Yanıt bekleyen", icon: Clock, tone: "text-sky-400" },
        { key: "reddedilen", label: "Reddeden", icon: XCircle, tone: "text-rose-400" },
        { key: "bekleyen", label: "Gönderim bekleyen", icon: Send, tone: "text-amber-400" },
        { key: "bu_ay_gonderilen", label: "Bu ay", icon: FileCheck2, tone: "text-violet-400" },
        { key: "onay_orani_yuzde", label: "Onay %", icon: Shield, tone: "text-emerald-300" },
        { key: "hatirlatma_bekleyen", label: "Hatırlatma bekleyen", icon: Clock, tone: "text-orange-300" },
    ];

    return (
        <section className="space-y-4">
            <div className="rounded-xl border border-violet-500/25 bg-gradient-to-br from-[#1a1030]/80 to-[#0b1437]/80 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 className="text-sm font-semibold text-[#f5ecd6] flex items-center gap-2">
                            <Scale className="h-4 w-4 text-violet-400" />
                            Yatar Dosyası WhatsApp Onayları
                        </h2>
                        <p className="text-xs text-[#8b93ae] mt-1 max-w-2xl">
                            Müvekkil kaydı sonrası yasal bilgilendirme metni gönderilir. EVET/HAYIR yanıtları
                            denetim kaydına işlenir. Test modu açıkken yalnızca test numarasına gider.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={yukle}
                            disabled={loading}
                            className="text-xs h-8 border-violet-500/30"
                        >
                            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                            Yenile
                        </Button>
                        <Button
                            size="sm"
                            onClick={testGonder}
                            disabled={busyKey === "yatar-test"}
                            className="text-xs h-8 bg-violet-600 hover:bg-violet-500"
                        >
                            <Send className="h-3.5 w-3.5 mr-1" />
                            Test numarasına gönder
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
                    {kartlar.map(({ key, label, icon: Icon, tone }) => (
                        <div
                            key={key}
                            className="rounded-lg border border-white/10 bg-black/20 px-3 py-2.5"
                        >
                            <div className="flex items-center gap-2 text-[10px] text-[#8b93ae] uppercase tracking-wide">
                                <Icon className={`h-3.5 w-3.5 ${tone}`} />
                                {label}
                            </div>
                            <div className="text-xl font-display text-[#F3E5AB] mt-1">
                                {ozet?.[key] ?? "—"}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
                <select
                    value={filtre}
                    onChange={(e) => setFiltre(e.target.value)}
                    className="rounded-lg border border-white/10 bg-[#0b1437] px-3 py-1.5 text-xs text-[#e2e8f0]"
                >
                    <option value="">Tüm durumlar</option>
                    <option value="ONAYLANDI">Onaylayanlar</option>
                    <option value="YANIT_BEKLIYOR">Yanıt bekleyen</option>
                    <option value="REDDEDILDI">Reddedenler</option>
                    <option value="BEKLIYOR">Gönderim bekleyen</option>
                </select>
                <input
                    type="search"
                    placeholder="Ad, TC, telefon, dosya no..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="rounded-lg border border-white/10 bg-[#0b1437] px-3 py-1.5 text-xs text-[#e2e8f0] min-w-[220px]"
                />
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 rounded-xl border border-white/10 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-white/5 text-[10px] uppercase text-[#8b93ae]">
                            <tr>
                                <th className="text-left px-3 py-2">Müvekkil</th>
                                <th className="text-left px-3 py-2">Durum</th>
                                <th className="text-left px-3 py-2 hidden md:table-cell">Yanıt</th>
                                <th className="text-right px-3 py-2">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {kayitlar.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-3 py-8 text-center text-[#8b93ae] text-xs">
                                        Kayıt bulunamadı.
                                    </td>
                                </tr>
                            )}
                            {kayitlar.map((row) => (
                                <tr
                                    key={row.id}
                                    className={`border-t border-white/5 hover:bg-white/[0.03] cursor-pointer ${secili?.id === row.id ? "bg-violet-500/10" : ""}`}
                                    onClick={() => secKayit(row)}
                                >
                                    <td className="px-3 py-2.5">
                                        <div className="font-medium text-[#f5ecd6] text-xs">{row.muvekkil_ad}</div>
                                        <div className="text-[10px] text-[#8b93ae]">{row.telefon}</div>
                                        {row.dosya_no && (
                                            <div className="text-[10px] text-violet-300/80">{row.dosya_no}</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <DurumPill durum={row.durum} label={row.durum_label} />
                                    </td>
                                    <td className="px-3 py-2.5 hidden md:table-cell text-[10px] text-[#8b93ae] max-w-[220px]">
                                        {row.yanit_ham_mesaj || row.yanit_metni ? (
                                            <span className="block truncate text-emerald-200/90" title={row.yanit_ham_mesaj || row.yanit_metni}>
                                                «{row.yanit_ham_mesaj || row.yanit_metni}»
                                            </span>
                                        ) : (
                                            "—"
                                        )}
                                        {row.yanit_telefon && (
                                            <span className="block text-[#8b93ae] mt-0.5">{row.yanit_telefon}</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2.5 text-right">
                                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                            {row.durum === "YANIT_BEKLIYOR" && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => panelOnay(row.id)}
                                                        className="px-2 py-1 rounded text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                                                    >
                                                        Onayla
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => panelRed(row.id)}
                                                        className="px-2 py-1 rounded text-[10px] bg-rose-500/20 text-rose-300 border border-rose-500/30"
                                                    >
                                                        Reddet
                                                    </button>
                                                </>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => kayitSil(row.id)}
                                                className="px-2 py-1 rounded text-[10px] bg-slate-500/20 text-slate-300 border border-slate-500/30 hover:bg-rose-500/20 hover:text-rose-300 hover:border-rose-500/30"
                                                title="Kaydı sil"
                                            >
                                                <Trash2 className="h-3 w-3 inline" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="rounded-xl border border-white/10 bg-[#0b1437]/60 p-4 space-y-3">
                    <h3 className="text-xs font-semibold text-[#F3E5AB] flex items-center gap-2">
                        <Shield className="h-4 w-4 text-violet-400" />
                        Denetim izi
                    </h3>
                    {!secili && (
                        <p className="text-xs text-[#8b93ae]">Detay için listeden bir kayıt seçin.</p>
                    )}
                    {secili && (
                        <>
                            <div className="text-xs space-y-1 text-[#c5cad8]">
                                <div>
                                    <strong className="text-[#F3E5AB]">{secili.muvekkil_ad}</strong>
                                </div>
                                <div>TC: {secili.tc_kimlik_no || "—"}</div>
                                <div>Kayıt: {new Date(secili.olusturma_tarihi).toLocaleString("tr-TR")}</div>
                                {secili.yanit_ham_mesaj && (
                                    <div className="mt-2 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                                        <div className="text-[10px] text-emerald-300 uppercase mb-1">Müvekkilin yazdığı mesaj</div>
                                        <div className="text-xs text-[#f5ecd6] whitespace-pre-wrap">«{secili.yanit_ham_mesaj}»</div>
                                        {secili.yanit_telefon && (
                                            <div className="text-[10px] text-[#8b93ae] mt-1">Tel: {secili.yanit_telefon}</div>
                                        )}
                                        {secili.yanit_tarihi && (
                                            <div className="text-[10px] text-[#8b93ae]">
                                                {new Date(secili.yanit_tarihi).toLocaleString("tr-TR")}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {secili.yanit_tarihi && !secili.yanit_ham_mesaj && (
                                    <div>Yanıt: {new Date(secili.yanit_tarihi).toLocaleString("tr-TR")}</div>
                                )}
                                <div className="flex items-center gap-1">
                                    <FileCheck2 className="h-3 w-3" />
                                    Metin versiyonu: {secili.yasal_metin_versiyon}
                                </div>
                            </div>
                            <div className="border-t border-white/10 pt-2 space-y-2 max-h-[320px] overflow-y-auto">
                                {denetim.length === 0 && (
                                    <p className="text-[10px] text-[#8b93ae]">Denetim kaydı yok.</p>
                                )}
                                {denetim.map((d) => (
                                    <div
                                        key={d.id}
                                        className="rounded-lg border border-white/5 bg-black/20 px-2.5 py-2 text-[10px]"
                                    >
                                        <div className="flex justify-between gap-2">
                                            <span className="font-semibold text-violet-300">{d.islem}</span>
                                            <span className="text-[#8b93ae] shrink-0">
                                                {new Date(d.tarih).toLocaleString("tr-TR")}
                                            </span>
                                        </div>
                                        {d.aciklama && (
                                            <p className="text-[#c5cad8] mt-1">{d.aciklama}</p>
                                        )}
                                        {d.kullanici && (
                                            <p className="text-[#8b93ae] mt-0.5">@{d.kullanici}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </section>
    );
}
